import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import billingService from '../services/billingService';

const BillingContext = createContext();

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

export const BillingProvider = ({ children }) => {
  const [userActivePlan, setUserActivePlan] = useState(null);
  const [userPlanLoading, setUserPlanLoading] = useState(false);
  const [userPlanError, setUserPlanError] = useState(null);
  const hasAttemptedFetch = useRef(false);
  const userPlanRequestRef = useRef(null);

  const fetchUserActivePlan = useCallback(async () => {
    if (hasAttemptedFetch.current) {
      return;
    }

    if (userPlanRequestRef.current) {
      return userPlanRequestRef.current;
    }

    try {
      setUserPlanLoading(true);
      setUserPlanError(null);
      hasAttemptedFetch.current = true;
      
      userPlanRequestRef.current = billingService.getUserActivePlan();
      const result = await userPlanRequestRef.current;
      
      if (result.success) {
        setUserActivePlan(result.data);
      } else {
        setUserActivePlan(null);
      }
    } catch (error) {
      setUserPlanError(error);
      setUserActivePlan(null);
    } finally {
      setUserPlanLoading(false);
      userPlanRequestRef.current = null;
    }
  }, []);

  const resetBillingState = useCallback(() => {
    setUserActivePlan(null);
    setUserPlanError(null);
    hasAttemptedFetch.current = false;
    userPlanRequestRef.current = null;
  }, []);

  const value = {
    userActivePlan,
    userPlanLoading,
    userPlanError,
    fetchUserActivePlan,
    resetBillingState,
    hasActivePlan: !!userActivePlan
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};
